import Konva from "konva";

interface ExportOptions {
  pixelRatio?: number;
  /**
   * Flattened behind the content so theme-colored strokes (arrows,
   * labels) stay readable wherever the PNG ends up. Omit for a
   * transparent background.
   */
  backgroundColor?: string;
  /** Padding around the content bounds, in on-screen pixels. */
  padding?: number;
}

/**
 * Render the full content of the canvas stage to a PNG data URL.
 *
 * Unlike a plain `stage.toDataURL()`, this captures everything on the
 * stage — including content panned outside the visible viewport — by
 * temporarily resizing/repositioning the stage to the content bounds,
 * then restoring it. All of that happens synchronously within one
 * frame, so nothing flickers on screen.
 *
 * Returns null when the stage has no drawable content.
 */
export function exportCanvasImage(
  stage: Konva.Stage,
  { pixelRatio = 2, backgroundColor, padding = 16 }: ExportOptions = {}
): string | null {
  const content = stage.getClientRect();
  if (content.width === 0 || content.height === 0) {
    return null;
  }

  const prev = {
    x: stage.x(),
    y: stage.y(),
    width: stage.width(),
    height: stage.height(),
  };

  let bgLayer: Konva.Layer | null = null;
  try {
    stage.width(content.width + padding * 2);
    stage.height(content.height + padding * 2);
    stage.x(prev.x - content.x + padding);
    stage.y(prev.y - content.y + padding);

    if (backgroundColor) {
      // The layer inherits the stage transform, so the rect must cover
      // the exported viewport in canvas coordinates.
      const scale = stage.scaleX() || 1;
      bgLayer = new Konva.Layer({ listening: false });
      bgLayer.add(
        new Konva.Rect({
          x: -stage.x() / scale,
          y: -stage.y() / scale,
          width: stage.width() / scale,
          height: stage.height() / scale,
          fill: backgroundColor,
        })
      );
      stage.add(bgLayer);
      bgLayer.moveToBottom();
    }

    stage.draw();
    return stage.toDataURL({ pixelRatio });
  } finally {
    bgLayer?.destroy();
    stage.setAttrs(prev);
    stage.batchDraw();
  }
}
